import { useEffect } from 'react'
import { Plus, Trash2, HelpCircle, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type {
  OutreachStrategy,
  SequenceStep,
  SequenceStepType,
  StyleSamplesMode,
} from './types'

const STEP_TYPES: Array<{ value: SequenceStepType; label: string; hint: string }> = [
  {
    value: 'connection_request',
    label: 'Connection Request',
    hint: 'Plain LinkedIn invite (no note attached) sent to the lead first. They have to accept before any DMs can land.',
  },
  {
    value: 'first_dm',
    label: 'First DM',
    hint: 'The first direct message after the lead accepts the connection. This is your warm intro, written in your voice.',
  },
  {
    value: 'follow_up_1',
    label: 'Follow-up 1',
    hint: 'First nudge if the lead did not reply to the first DM. The AI never re-pitches here. It drops a new angle, a sourced observation, or a restated question.',
  },
  {
    value: 'follow_up_2',
    label: 'Follow-up 2',
    hint: 'Second nudge. On senior leads this is where the pitch is revealed as natural context. On mid/junior leads the AI names the product directly and proposes a call.',
  },
  {
    value: 'follow_up_3',
    label: 'Follow-up 3',
    hint: 'Last directive touch before the breakup message. Direct yes/no or invitation-to-be-proven-wrong close. Calendar offer allowed.',
  },
]

/**
 * Per-strategy explainer. Hover the (?) on each strategy in the Select to see
 * this — these match the SKILL framework's Style mapping in the production AI
 * worker (workers/ai/src/prompts/outreach.ts STRATEGY_PROMPTS).
 */
const STRATEGIES: Array<{ value: OutreachStrategy; label: string; description: string; bestFor: string }> = [
  {
    value: 'pain_agitate',
    label: 'Pain Agitate',
    description:
      'Opens with a specific pain the lead is dealing with, then names the second-order cost most teams miss.',
    bestFor: 'Leads where the pain is concrete and named in their profile (e.g., "SDR over-hiring", "low reply rates").',
  },
  {
    value: 'mutual_value',
    label: 'Mutual Value',
    description:
      'Frames the conversation as a two-way insight exchange instead of a pitch. The AI offers a pattern, benchmark, or under-discussed angle on their problem. No product name on first touch.',
    bestFor: 'Senior leads who hate being pitched. Strong for peer-to-peer introductions.',
  },
  {
    value: 'social_proof',
    label: 'Social Proof',
    description:
      'Anchors the pitch in real numbers and named outcomes (e.g., "3x acceptance vs. batch tools", "95%+ seen rates"). The AI uses concrete proof from your offer settings and will not invent stats.',
    bestFor: 'Competitive markets where the lead is comparing tools. Best on mid/junior buyers who want validation.',
  },
  {
    value: 'curiosity_gap',
    label: 'Curiosity Gap',
    description:
      'Opens a tactical question that nudges the lead to think about the offer category without naming the product. No "we help" body. The reveal lands in the follow-up.',
    bestFor: 'Sophisticated, hard-to-reach leads who tune out direct pitches. Lowest friction to reply.',
  },
  {
    value: 'direct_expert',
    label: 'Direct Expert',
    description:
      'A sharp, slightly contrarian peer-level observation about the lead\'s specific company × role. Positions you as an expert, not a vendor.',
    bestFor: 'Technical buyers, ops leaders, and subject-matter experts who want substance over polish.',
  },
  {
    value: 'challenge',
    label: 'Challenge',
    description:
      'Partial agreement → "But/However" pivot → seed of a counter-version → invitation to be proven wrong. Use only when the lead has posted an opinion or hot take.',
    bestFor: 'Opinionated thought leaders. Senior leads pride themselves on updating their views.',
  },
  {
    value: 'trojan_horse',
    label: 'Trojan Horse',
    description:
      'Leads with a free resource (audit, teardown, framework), not the product. The CTA is permission to send only. The product comes up only if they engage.',
    bestFor: 'Skeptical leads who\'ve been burned by outbound. Value-first builds trust before the pitch.',
  },
  {
    value: 'mutual_connection',
    label: 'Mutual Connection',
    description:
      'References a real shared context (group, mutual contact, community thread). The AI will not invent a connection and only uses one if it shows up in the lead profile.',
    bestFor: 'Leads with visible shared groups, alumni networks, or named mutual contacts on LinkedIn.',
  },
]

const STRATEGY_LABEL: Record<OutreachStrategy, string> =
  STRATEGIES.reduce(
    (acc, s) => {
      acc[s.value] = s.label
      return acc
    },
    {} as Record<OutreachStrategy, string>,
  )

const MAX_STEPS = 5
const MAX_SAMPLES_PER_SLOT = 3
const MAX_SAMPLE_LEN = 600

/**
 * Hard lock — the first 2 steps of EVERY sequence are fixed to:
 *   index 0: connection_request
 *   index 1: first_dm
 *
 * Why: LinkedIn outreach via Unipile only works in the connect → accept → DM
 * flow. The first DM writes into the chat LinkedIn creates ONLY after the
 * connection request is accepted. Allowing customers to reorder/skip these
 * produces silently-broken campaigns. The backend enforces the same rule —
 * see workers/campaigns/src/routes/sequence.ts:assertLockedFirstSteps.
 */
const LOCKED_STEP_COUNT = 2
const LOCKED_STEP_TYPES: readonly SequenceStepType[] = ['connection_request', 'first_dm']

const DEFAULT_STEP_FOR_INDEX: Record<number, SequenceStepType> = {
  0: 'connection_request',
  1: 'first_dm',
}

const DEFAULT_STEP: SequenceStep = {
  type: 'follow_up_1',
  delay_days: 3,
  strategy: 'pain_agitate',
  style_samples: [],
}

type SequenceBuilderProps = {
  readonly steps: readonly SequenceStep[]
  readonly onChange: (steps: readonly SequenceStep[]) => void
  /** Mode for applying user style samples. Defaults to 'global'. */
  readonly styleSamplesMode?: StyleSamplesMode
  readonly onStyleSamplesModeChange?: (mode: StyleSamplesMode) => void
  /** Sample messages used when mode === 'global'. Up to 3. */
  readonly styleSamplesGlobal?: readonly string[]
  readonly onStyleSamplesGlobalChange?: (samples: readonly string[]) => void
}

/**
 * Helper: edit a single sample inside an array, returning a new array.
 * If `index === array.length`, appends a new element. Empty trailing slots
 * are tolerated so the user can type into them.
 */
function setArrayElement(arr: readonly string[], index: number, value: string): string[] {
  const next = [...arr]
  next[index] = value
  return next
}

function removeArrayElement(arr: readonly string[], index: number): string[] {
  return arr.filter((_, i) => i !== index)
}

/**
 * Lead placeholders the backend substitutes inside writing samples before
 * they hit the AI prompt. Surface these in the UI so users know they can
 * write "Hey {first_name}" instead of a fake name.
 *
 * Must stay in sync with v3/workers/ai/src/prompts/placeholders.ts.
 */
const LEAD_PLACEHOLDERS = [
  '{first_name}',
  '{full_name}',
  '{last_name}',
  '{company}',
  '{title}',
  '{industry}',
  '{location}',
] as const

/**
 * Stable per-strategy sample editor. The number of slots is fixed at
 * MAX_SAMPLES_PER_SLOT so users can fill in 1, 2, or 3 freely without
 * needing to click "add". Empty slots are filtered out before persisting.
 */
function StyleSamplesEditor({
  label,
  hint,
  samples,
  onChange,
}: {
  readonly label: string
  readonly hint: string
  readonly samples: readonly string[]
  readonly onChange: (samples: readonly string[]) => void
}) {
  // Pad with empty strings so we always render MAX slots.
  const padded: string[] = [...samples]
  while (padded.length < MAX_SAMPLES_PER_SLOT) padded.push('')

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <Label className="text-xs">{label}</Label>
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      </div>
      <div className="flex flex-col gap-1 rounded-md border border-dashed bg-muted/20 px-2.5 py-2">
        <p className="text-[11px] font-medium text-muted-foreground">
          Use variables for the lead's data, replaced at send time:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_PLACEHOLDERS.map((token) => (
            <code
              key={token}
              className="rounded bg-background px-1.5 py-0.5 text-[10px] font-mono text-foreground/80"
            >
              {token}
            </code>
          ))}
        </div>
      </div>
      {padded.map((value, i) => (
        <div key={i} className="flex items-start gap-2">
          <textarea
            className="flex-1 min-h-[60px] max-h-[140px] rounded-md border bg-background px-2 py-1.5 text-xs resize-y placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder={
              i === 0
                ? 'e.g. "Hey {first_name}, saw {company} just expanded into APAC, how are you handling the new hiring load?"'
                : `Sample ${i + 1} (max ${MAX_SAMPLE_LEN} chars)`
            }
            value={value}
            maxLength={MAX_SAMPLE_LEN}
            onChange={(e) => {
              const next = setArrayElement(padded, i, e.target.value)
              // Trim trailing empties so we don't persist meaningless slots.
              while (next.length > 0 && (next[next.length - 1] ?? '').trim() === '') {
                next.pop()
              }
              onChange(next)
            }}
          />
          {value.trim().length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive mt-1"
              onClick={() => onChange(removeArrayElement(padded, i).filter((s) => s !== ''))}
              aria-label={`Remove sample ${i + 1}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      ))}
    </div>
  )
}

export function SequenceBuilder({
  steps,
  onChange,
  styleSamplesMode = 'global',
  onStyleSamplesModeChange,
  styleSamplesGlobal = [],
  onStyleSamplesGlobalChange,
}: SequenceBuilderProps) {
  // Auto-correct any pre-existing campaign whose first 2 steps don't conform
  // to the lock (legacy data from before 2026-06-06). Runs once on mount and
  // any time the parent passes in a different `steps` array — idempotent
  // since the comparison short-circuits when types already match.
  useEffect(() => {
    const needsFix =
      steps.length < LOCKED_STEP_COUNT ||
      LOCKED_STEP_TYPES.some((t, i) => steps[i]?.type !== t)
    if (!needsFix) return
    const next: SequenceStep[] = [...steps]
    for (let i = 0; i < LOCKED_STEP_COUNT; i++) {
      const lockedType = LOCKED_STEP_TYPES[i]!
      if (!next[i]) {
        next[i] = {
          ...DEFAULT_STEP,
          type: lockedType,
          // Connection requests must fire on day 0; first DMs default to
          // day 0 too (they pull from the same orchestrator tick once the
          // accept reconciliation lands).
          delay_days: 0,
        }
      } else if (next[i]!.type !== lockedType) {
        next[i] = { ...next[i]!, type: lockedType }
      }
    }
    onChange(next)
    // Deps intentionally only include `steps`. `onChange` is a prop callback
    // whose identity can shift between renders without the underlying intent
    // changing — adding it would trigger this effect on every render and
    // create an update loop.
  }, [steps])

  function addStep() {
    if (steps.length >= MAX_STEPS) return
    // If the user is adding step 1 or 2, force the locked type so the
    // sequence is always conformant even mid-build. After that, the
    // generic default kicks in.
    const lockedType = DEFAULT_STEP_FOR_INDEX[steps.length]
    const next: SequenceStep = lockedType
      ? { ...DEFAULT_STEP, type: lockedType, delay_days: 0 }
      : { ...DEFAULT_STEP }
    onChange([...steps, next])
  }

  function removeStep(index: number) {
    // Belt-and-suspenders: the delete button is also disabled for these
    // indices, but if anything ever bypasses the UI guard (programmatic
    // call, future refactor) we still refuse to drop the locked steps.
    if (index < LOCKED_STEP_COUNT) return
    onChange(steps.filter((_, i) => i !== index))
  }

  function updateStep(index: number, patch: Partial<SequenceStep>) {
    // Reject type changes to locked positions. delay_days / strategy /
    // style_samples are still editable on rows 1 and 2.
    if (index < LOCKED_STEP_COUNT && patch.type && patch.type !== LOCKED_STEP_TYPES[index]) {
      return
    }
    onChange(
      steps.map((step, i) => (i === index ? { ...step, ...patch } : step)),
    )
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Style samples mode selector — top of the panel so users see the
          decision before configuring individual steps. */}
      {onStyleSamplesModeChange && (
        <div className="rounded-lg border bg-card p-3 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-sm font-semibold">Your writing style</Label>
            <p className="text-xs text-muted-foreground">
              Paste 1-3 messages you have actually written. The AI will mimic your tone and cadence,
              not the corporate template look. Use placeholders like{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-[10px] font-mono">{'{first_name}'}</code>
              {' '}or{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-[10px] font-mono">{'{company}'}</code>
              {' '}where the lead's real data should go.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={styleSamplesMode === 'global' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => onStyleSamplesModeChange('global')}
            >
              One set for all steps
            </Button>
            <Button
              type="button"
              size="sm"
              variant={styleSamplesMode === 'per_step' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => onStyleSamplesModeChange('per_step')}
            >
              Customize per step
            </Button>
          </div>
          {styleSamplesMode === 'global' && onStyleSamplesGlobalChange && (
            <StyleSamplesEditor
              label="Sample messages (used for every step)"
              hint="Paste up to 3. Short, casual, in your real voice. Leave fields blank you do not need."
              samples={styleSamplesGlobal}
              onChange={onStyleSamplesGlobalChange}
            />
          )}
          {styleSamplesMode === 'per_step' && (
            <p className="text-xs text-muted-foreground">
              Each step below has its own sample box. Different strategies can have different example messages.
            </p>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="relative flex flex-col gap-0">
        {steps.map((step, index) => {
          const isLocked = index < LOCKED_STEP_COUNT
          return (
          <div key={index} className="relative flex gap-3">
            {/* Timeline connector */}
            <div className="flex flex-col items-center">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-background text-xs font-bold text-primary">
                {index + 1}
              </div>
              {index < steps.length - 1 && (
                <div className="w-0.5 flex-1 bg-border my-1" />
              )}
            </div>

            {/* Step config */}
            <div className="flex-1 pb-4">
              <div className="rounded-lg border bg-card p-3 flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-1">
                      <Label className="text-xs">Step Type</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center text-muted-foreground hover:text-foreground"
                            aria-label="What does this step type do?"
                          >
                            <HelpCircle className="h-3 w-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          {STEP_TYPES.find((t) => t.value === step.type)?.hint
                            ?? 'Determines what message the AI generates for this slot in the sequence.'}
                        </TooltipContent>
                      </Tooltip>
                      {isLocked && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="ml-auto inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                              aria-label="This step is locked"
                            >
                              <Lock className="h-2.5 w-2.5" />
                              Required
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs">
                            Every LinkedIn campaign must start with a connection request, then a first DM after the lead accepts. This is locked to keep the sequence functional.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <Select
                      value={step.type}
                      onValueChange={(v) =>
                        updateStep(index, { type: v as SequenceStepType })
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STEP_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value} className="text-xs">
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">
                      {index === 0 ? 'Send on day' : 'Delay (days after previous)'}
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      step={1}
                      value={step.delay_days}
                      onChange={(e) => {
                        // Clamp to a non-negative integer in [0, 30].
                        // Number('abc') is NaN which would otherwise persist as
                        // delay_days=NaN — the backend then computes NaN for its
                        // delay window and `elapsed < NaN` is always false, so
                        // the step would fire on every 30s tick. Ugly rate-limit risk.
                        const raw = Number(e.target.value)
                        const safe = Number.isFinite(raw)
                          ? Math.max(0, Math.min(30, Math.floor(raw)))
                          : step.delay_days
                        updateStep(index, { delay_days: safe })
                      }}
                      className="h-8 text-xs"
                    />
                  </div>
                  {index > 0 && (
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-xs text-[var(--color-silver)]">
                        or hours (fast test)
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        max={720}
                        step={0.5}
                        placeholder="-"
                        value={step.delay_hours ?? ''}
                        onChange={(e) => {
                          // Empty clears the override (falls back to delay_days).
                          // A valid non-negative number (fractional ok) wins over
                          // delay_days in the backend eligibility gate.
                          const val = e.target.value.trim()
                          if (val === '') {
                            updateStep(index, { delay_hours: undefined })
                            return
                          }
                          const raw = Number(val)
                          if (Number.isFinite(raw) && raw >= 0) {
                            updateStep(index, { delay_hours: Math.min(720, raw) })
                          }
                        }}
                        className="h-8 w-24 text-xs"
                        title="Overrides the day delay with an hour delay. Leave blank for normal day-based cadence. Used for compressed test runs (e.g. 3 = wait 3 hours)."
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-end gap-2">
                  <div className="flex-1 flex flex-col gap-1.5">
                    <div className="flex items-center gap-1">
                      <Label className="text-xs">Strategy</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center text-muted-foreground hover:text-foreground"
                            aria-label="How this strategy works"
                          >
                            <HelpCircle className="h-3 w-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-sm text-xs">
                          {(() => {
                            const meta = STRATEGIES.find((s) => s.value === step.strategy)
                            if (!meta) return 'Pick how the AI should approach this message.'
                            return (
                              <div className="flex flex-col gap-1.5">
                                <p className="font-semibold">{meta.label}</p>
                                <p>{meta.description}</p>
                                <p className="text-muted-foreground">
                                  <span className="font-medium">Best for:</span> {meta.bestFor}
                                </p>
                              </div>
                            )
                          })()}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Select
                      value={step.strategy}
                      onValueChange={(v) =>
                        updateStep(index, { strategy: v as OutreachStrategy })
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-w-[420px]">
                        {STRATEGIES.map((s) => (
                          <SelectItem key={s.value} value={s.value} className="text-xs">
                            <div className="flex flex-col gap-0.5 py-0.5">
                              <span className="font-medium">{s.label}</span>
                              <span className="text-[10px] text-muted-foreground line-clamp-2 max-w-[360px]">
                                {s.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeStep(index)}
                    disabled={isLocked || steps.length <= 1}
                    aria-label={
                      isLocked
                        ? `Step ${index + 1} is required and cannot be removed`
                        : `Remove step ${index + 1}`
                    }
                    title={
                      isLocked
                        ? `Step ${index + 1} is required and cannot be removed`
                        : undefined
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Per-step style samples — only when mode is per_step */}
                {styleSamplesMode === 'per_step' && (
                  <div className="border-t pt-3">
                    <StyleSamplesEditor
                      label={`Sample messages for ${STRATEGY_LABEL[step.strategy] ?? step.strategy}`}
                      hint="Paste up to 3 messages you have actually written for this kind of touch."
                      samples={step.style_samples ?? []}
                      onChange={(style_samples) => updateStep(index, { style_samples })}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          )
        })}
      </div>

      {/* Add step */}
      {steps.length < MAX_STEPS && (
        <Button variant="outline" size="sm" onClick={addStep} className="self-start">
          <Plus className="h-4 w-4 mr-1" />
          Add Step
        </Button>
      )}

      {steps.length >= MAX_STEPS && (
        <p className="text-xs text-muted-foreground">
          Maximum of {MAX_STEPS} steps reached.
        </p>
      )}
    </div>
  )
}
